import {
  WEEKLY_CONSENT,
  LEAD_SOURCES,
  type LeadInput,
} from "./customy/forms-contract";
import { trackAnalytics } from "./customy/analytics-client";

/** Component-local retries only. Never persist visitor PII in browser storage. */
export function createLeadSubmitter(
  transport: typeof fetch = fetch,
  uuid: () => string = () => crypto.randomUUID(),
) {
  let pending:
    | { payload: string; key: string; versionId: string | null }
    | undefined;
  let running: { payload: string; promise: Promise<unknown> } | undefined;
  const submit = async (input: LeadInput) => {
    const payload = JSON.stringify(input);
    if (pending && pending.payload !== payload) pending = undefined;
    if (!pending) {
      const metadata = await transport(
        `/api/leads?source=${encodeURIComponent(input.source)}`,
        { cache: "no-store", redirect: "error" },
      );
      if (!metadata.ok) throw new Error("FORM_UNAVAILABLE");
      const descriptor = await metadata.json();
      if (
        !["legacy", "published"].includes(descriptor.mode) ||
        (descriptor.mode === "published" &&
          typeof descriptor.versionId !== "string") ||
        descriptor.consentText !==
          (LEAD_SOURCES[input.source] === "subscription"
            ? WEEKLY_CONSENT
            : null)
      )
        throw new Error("FORM_CHANGED");
      pending = { payload, key: uuid(), versionId: descriptor.versionId };
    }
    const response = await transport("/api/leads", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": pending.key,
      },
      body: JSON.stringify({
        ...input,
        ...(pending.versionId ? { versionId: pending.versionId } : {}),
      }),
      redirect: "error",
    });
    // Keep both version and key on uncertain delivery. A publication change
    // requires reloading; silently minting a new key could submit twice.
    if (!response.ok)
      throw new Error(
        response.status === 409 ? "FORM_CHANGED_RELOAD" : "FORM_UNAVAILABLE",
      );
    const result = await response.json();
    if (result.ok !== true || result.state !== "received")
      throw new Error("FORM_RESPONSE_INVALID");
    const intent = LEAD_SOURCES[input.source];
    void trackAnalytics(intent === "subscription" ? "codifly.subscription_requested" : intent === "commercial" ? "codifly.commercial_requested" : "codifly.contact_requested", { pageType: "other" });
    return result;
  };
  return (input: LeadInput): Promise<unknown> => {
    const payload = JSON.stringify(input);
    if (running)
      return running.payload === payload
        ? running.promise
        : Promise.reject(new Error("FORM_BUSY"));
    const promise = submit(input).finally(() => {
      running = undefined;
    });
    running = { payload, promise };
    return promise;
  };
}
